import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { GestionclientesService } from '../gestionclientes.service';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { Prospeccion } from '../models/prospeccion';
import { ModalSeguimientoComponent } from './modal-seguimiento/modal-seguimiento.component';
import { ModalRegistroSeguimientoComponent } from './modal-registro-seguimiento/modal-registro-seguimiento.component';

@Component({
  selector: 'app-gestion-prospeccion',
  standalone: true,
  templateUrl: './gestion-prospeccion.component.html',
  imports: [NgIf, NgFor, NgClass, FormsModule, DatePipe, MatFormFieldModule, MatInputModule, MatButtonModule],
})
export class GestionProspeccionComponent implements OnInit {
  listaProspectos: Array<Prospeccion> = [];
  shed = new ConsultaList<Prospeccion>();
  totalRegistros = 0;
  totalPages = 0;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  currentPage = 1;
  loading = false;

  constructor(
    private dialog: MatDialog,
    private clientService: GestionclientesService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.shed.bean = new Prospeccion();
    this.consultaPagina(1);
  }

  consultaPagina(page: number): void {
    this.currentPage = page;
    this.loading = this.listaProspectos.length === 0;
    this.cdr.detectChanges();
    this.shed.regInicial = (page - 1) * this.pageSize;
    this.shed.regXPagina = this.pageSize;

    this.clientService
      .consultaProspectos(this.shed)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res) => {
          this.totalRegistros = this.resolveTotalRegistros(res);
          this.listaProspectos = [...(Array.isArray(res?.list) ? res.list : [])];
          this.totalPages =
            this.totalRegistros > 0
              ? Math.ceil(this.totalRegistros / this.pageSize)
              : 0;

          if (this.totalPages > 0 && this.currentPage > this.totalPages) {
            this.consultaPagina(this.totalPages);
            return;
          }

          this.cdr.detectChanges();
        },
        error: () => {
          this.listaProspectos = [];
          this.totalRegistros = 0;
          this.totalPages = 0;
          this.cdr.detectChanges();
        },
      });
  }

  modSegumiento(item: Prospeccion): void {
    const dialogRef = this.dialog.open(ModalSeguimientoComponent, {
      height: '900px',
      width: '1300px',
      panelClass: ['custom-modalbox', 'prospeccion-consulta-dialog'],
      data: { item, prospectos: null },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.consultaPagina(this.currentPage);
    });
  }

  agregarProspecto(): void {
    const dialogRef = this.dialog.open(ModalSeguimientoComponent, {
      maxHeight: '1000px',
      width: '1200px',
      panelClass: 'custom-modalbox',
      data: { item: null, prospectos: this.listaProspectos },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.consultaPagina(this.currentPage);
    });
  }

  finSeguimiento(item: Prospeccion): void {
    const dialogRef = this.dialog.open(ModalRegistroSeguimientoComponent, {
      width: '650px',
      panelClass: 'custom-modalbox',
      // El modal edita su propia copia para no modificar la fila durante la detección de cambios.
      data: { ...item },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.consultaPagina(this.currentPage);
    });
  }

  filtroBusqueda(filtro: string): void {
    this.shed.param1 = filtro?.trim() ? filtro.trim() : null;
    this.consultaPagina(1);
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultaPagina(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultaPagina(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getStatusLabel(prospecto: Prospeccion): string {
    return prospecto?.estatus === 'CERRADO' ? 'Cerrado' : prospecto?.estatus || 'Activo';
  }

  getStatusClass(prospecto: Prospeccion): string {
    return prospecto?.estatus === 'CERRADO' ? 'is-inactive' : 'is-active';
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);
    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }

  private resolveTotalRegistros(res: ConsultaList<Prospeccion>): number {
    const response = res as ConsultaList<Prospeccion> & Record<string, unknown>;
    const candidates = [
      response?.pTotalItems,
      response?.totalItems,
      response?.totalRegistros,
      response?.param4,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    const currentItems = Array.isArray(res?.list) ? res.list.length : 0;
    if (currentItems === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + currentItems;
  }
}
